import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Stethoscope, 
  PawPrint, 
  User, 
  Edit, 
  Trash2,
  X,
  Calendar,
  ChevronUp,
  ChevronDown,
  Syringe,
  Activity,
  Save
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getExaminations, addExamination, deleteExamination, updateExamination } from '../services/examinationService';
import { getPets } from '../services/petService';
import { getOwners } from '../services/ownerService';
import EmptyState from '../components/EmptyState/EmptyState';
import DeleteConfirmModal from '../components/Modals/DeleteConfirmModal';
import { getAppointments } from '../services/appointmentService';
import { getVaccinations } from '../services/vaccinationService';
import { Examination, Pet, Owner, Appointment, Vaccination } from '../types';
import { todayLocal } from '../utils/dateUtils';
import { normalizeTr } from '../utils/normalizeTr';

const Examinations: React.FC = () => {

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState<Pet & { owner?: Owner } | null>(null);
  const [sortField, setSortField] = useState<'date' | 'petName' | 'ownerName'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [formData, setFormData] = useState({
    pet_id: '',
    date: todayLocal(),
    symptoms: '',
    diagnosis: '',
    treatment: '',
    prescription: '',
    next_visit: ''
  });
  const [editingExam, setEditingExam] = useState<Examination | null>(null);

  const queryClient = useQueryClient();

  const { data: examinations = [], isLoading: isLoadingExaminations, isError: isErrorExaminations, refetch: fetchExaminations } = useQuery<Examination[]>({ queryKey: ['examinations'], queryFn: getExaminations });
  const { data: pets = [], isLoading: isLoadingPets, isError: isErrorPets } = useQuery<Pet[]>({ queryKey: ['pets'], queryFn: getPets });
  const { data: owners = [], isLoading: isLoadingOwners, isError: isErrorOwners } = useQuery<Owner[]>({ queryKey: ['owners'], queryFn: getOwners });
  const { data: appointments = [], isLoading: isLoadingAppointments, isError: isErrorAppointments } = useQuery<Appointment[]>({ queryKey: ['appointments'], queryFn: getAppointments });
  const { data: vaccinations = [], isLoading: isLoadingVaccinations, isError: isErrorVaccinations } = useQuery<Vaccination[]>({ queryKey: ['vaccinations'], queryFn: getVaccinations });

  const loading = isLoadingExaminations || isLoadingPets || isLoadingOwners || isLoadingAppointments || isLoadingVaccinations;
  const error = isErrorExaminations || isErrorPets || isErrorOwners || isErrorAppointments || isErrorVaccinations ? 'Veri yüklenirken bir hata oluştu.' : null;

  const getExaminationWithPet = (examination: Examination) => {
    const pet = pets.find(p => p.id === examination.pet_id || p.id === examination.petId);
    const owner = pet ? owners.find(o => o.id === pet.owner_id) : null;
    return { ...examination, pet, owner };
  };

  const handleSort = (field: 'date' | 'petName' | 'ownerName') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'date' | 'petName' | 'ownerName') => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  const filteredExaminations = examinations
    .map(getExaminationWithPet)
    .filter(examination => {
      const matchesSearch = 
        normalizeTr(examination.pet?.name).includes(normalizeTr(searchTerm)) ||
        normalizeTr(examination.pet?.owner?.name).includes(normalizeTr(searchTerm)) ||
        normalizeTr(examination.symptoms).includes(normalizeTr(searchTerm)) ||
        normalizeTr(examination.diagnosis).includes(normalizeTr(searchTerm));
      
      // Tarih araması düzeltildi
      const matchesDate = !selectedDate || examination.date === selectedDate;
      return matchesSearch && matchesDate;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'petName':
          aValue = a.pet?.name || '';
          bValue = b.pet?.name || '';
          break;
        case 'ownerName':
          aValue = a.pet?.owner?.name || '';
          bValue = b.pet?.owner?.name || '';
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleShowHistory = (examination: Examination & { pet?: Pet; owner?: Owner | null }) => {
    if (examination.pet) {
      const petWithOwner = {
        ...examination.pet,
        owner: owners.find(o => o.id === examination.pet?.owner_id) || undefined
      };
      setSelectedPet(petWithOwner);
      setShowHistoryModal(true);
    }
  };

  const getPetHistory = (petId: string) => {
    const petExaminations = examinations.filter(ex => ex.pet_id === petId || ex.petId === petId);
    const petAppointments = appointments.filter(apt => apt.pet_id === petId || apt.petId === petId);
    const petVaccinations = vaccinations.filter(vacc => vacc.pet_id === petId || vacc.petId === petId);

    const allHistory = [
      ...petExaminations.map(ex => ({
        ...ex,
        type: 'examination' as const,
        icon: Stethoscope,
        color: 'bg-purple-100 text-purple-800',
        label: 'Muayene'
      })),
      ...petAppointments.map(apt => ({
        ...apt,
        type: 'appointment' as const,
        icon: Calendar,
        color: 'bg-blue-100 text-blue-800',
        label: 'Randevu'
      })),
      ...petVaccinations.map(vacc => ({
        ...vacc,
        type: 'vaccination' as const,
        icon: Syringe,
        color: 'bg-green-100 text-green-800',
        label: 'Aşı'
      }))
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return allHistory;
  };

  const addExaminationMutation = useMutation({
    mutationFn: addExamination,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['examinations'] });
      toast.success('Muayene başarıyla eklendi!');
      setShowForm(false);
      setFormData({
        pet_id: '',
        date: todayLocal(),
        symptoms: '',
        diagnosis: '',
        treatment: '',
        prescription: '',
        next_visit: ''
      });
      setEditingExam(null);
    },
    onError: (error) => {
      toast.error('Muayene eklenirken hata oluştu.');
      console.error(error);
    }
  });

  const updateExaminationMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Examination> }) => updateExamination(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['examinations'] });
      toast.success('Muayene güncellendi!');
      setShowForm(false);
      setEditingExam(null);
    },
    onError: (error: any) => toast.error('Güncelleme hatası: ' + error.message)
  });

  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingExam) {
      updateExaminationMutation.mutate({ id: editingExam.id, data: formData as any });
    } else {
      addExaminationMutation.mutate(formData);
    }
  };

  const deleteExaminationMutation = useMutation({
    mutationFn: deleteExamination,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['examinations'] });
      toast.success('Muayene başarıyla silindi!');
    },
    onError: (error: any) => {
      toast.error(`Silinirken bir hata oluştu: ${error.message}`);
    }
  });

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [examToDelete, setExamToDelete] = useState<Examination | null>(null);

  const handleDeleteExamination = (examinationId: string) => {
    const exam = examinations.find(e => e.id === examinationId);
    if (exam) setExamToDelete(exam);
  };

  const confirmDeleteExam = async () => {
    if (!examToDelete) return;
    deleteExaminationMutation.mutate(examToDelete.id);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  if (loading) return <div className="text-center py-12">Yükleniyor...</div>;
  if (error) return <div className="text-center py-12 text-red-600">{error}</div>;

  return (
    <div className="page-stack">
      {/* Page Header */}
      <div className="page-head">
        <div>
          <h1 className="page-title">Muayeneler</h1>
          <p className="page-subtitle">Tüm muayene kayıtları</p>
        </div>
        <button 
          onClick={() => {
            setEditingExam(null);
            setFormData({
              pet_id: '',
              date: todayLocal(),
              symptoms: '',
              diagnosis: '',
              treatment: '',
              prescription: '',
              next_visit: ''
            });
            setShowForm(true);
          }}
          className="btn btn-primary flex items-center space-x-2 w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Muayene</span>
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="dribbble-panel p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#B0A99A] w-5 h-5" />
            <input
              type="text"
              placeholder="Muayene ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
        
        <div className="dribbble-panel p-4">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#B0A99A] w-5 h-5" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
              className="input pl-10"
              placeholder="Tarih seçin"
          />
          </div>
        </div>
      </div>

      {/* Empty state — tablo başlıkları olmadan */}
      {filteredExaminations.length === 0 ? (
        <div className="dribbble-panel overflow-hidden">
          <EmptyState
            icon={Stethoscope}
            iconColor="rose"
            title={examinations.length === 0 ? "Henüz muayene kaydı yok" : "Sonuç bulunamadı"}
            description={examinations.length === 0
              ? "Hayvanların muayene geçmişini detaylı kaydedin. Teşhis, tedavi ve reçeteleri kolayca takip edin."
              : "Arama kriterlerinize uygun muayene yok. Filtreleri temizleyip tekrar deneyin."}
            actionLabel={examinations.length === 0 ? "Yeni Muayene Kaydı" : undefined}
            onAction={examinations.length === 0 ? () => setShowForm(true) : undefined}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredExaminations.map((examination, index) => (
            <motion.div
              key={examination.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="dribbble-panel p-4 group relative"
            >
              {/* Üst — Stetoskop ikonu + Tarih */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-2xl bg-rose-100 flex items-center justify-center shrink-0">
                  <Stethoscope className="w-6 h-6 text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#2B2B2B] truncate" title={examination.diagnosis}>
                    {examination.diagnosis || 'Muayene'}
                  </p>
                  <p className="text-xs text-[#8A8676]">{formatDate(examination.date)}</p>
                </div>
              </div>

              {/* Hasta + Sahip rozetleri */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B]">
                  <PawPrint className="w-3 h-3" />
                  {examination.pet?.name}
                </span>
                {examination.pet?.owner?.name && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B]">
                    <User className="w-3 h-3" />
                    {examination.pet.owner.name}
                  </span>
                )}
              </div>

              {/* Tedavi özeti */}
              {examination.treatment && (
                <div className="mb-2 p-2 rounded-lg bg-[#E8EFD9] border border-[#D2DEB4]">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-[#3F5728] mb-0.5">Tedavi</p>
                  <p className="text-[11px] text-[#2B2B2B] line-clamp-2" title={examination.treatment}>
                    {examination.treatment}
                  </p>
                </div>
              )}

              {/* Belirtiler */}
              {examination.symptoms && (
                <div className="flex items-start gap-1.5 mb-2" title={examination.symptoms}>
                  <Activity className="w-3 h-3 text-[#B0A99A] shrink-0 mt-0.5" />
                  <p className="text-[11px] text-[#8A8676] line-clamp-2 italic flex-1">
                    {examination.symptoms}
                  </p>
                </div>
              )}

              {/* Sonraki kontrol */}
              {examination.next_visit && (
                <div className="flex items-center gap-1.5 text-[11px] text-amber-700 font-medium mb-2">
                  <Calendar className="w-3 h-3" />
                  Sonraki Kontrol · {formatDate(examination.next_visit)}
                </div>
              )}

              {/* Aksiyon — hover ile sağ üstte */}
              <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 rounded-lg p-1 shadow-sm border border-[#F0EBE1]">
                <button
                  onClick={() => handleShowHistory(examination)}
                  className="w-6 h-6 rounded-md hover:bg-blue-50 text-blue-600 flex items-center justify-center"
                  title="Geçmiş"
                >
                  <Activity className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setEditingExam(examination); setShowForm(true); setFormData({
                    pet_id: examination.pet_id || examination.petId || '',
                    date: examination.date,
                    symptoms: examination.symptoms,
                    diagnosis: examination.diagnosis,
                    treatment: examination.treatment,
                    prescription: examination.prescription,
                    next_visit: examination.next_visit || examination.nextVisit || '',
                  }); }}
                  className="w-6 h-6 rounded-md hover:bg-emerald-50 text-emerald-600 flex items-center justify-center"
                  title="Düzenle"
                >
                  <Edit className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDeleteExamination(examination.id)}
                  className="w-6 h-6 rounded-md hover:bg-red-50 text-red-600 flex items-center justify-center"
                  title="Sil"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      {/* Examination Form Modal */}
      {showForm && createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 22 }}
            className="dribbble-panel max-w-xl w-full max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E2D4] shrink-0 bg-white rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center text-white shadow-md shadow-rose-300/40">
                  <Stethoscope className="w-4 h-4" />
                </div>
                <h2 className="text-lg font-bold text-[#2B2B2B]">
                  {editingExam ? 'Muayeneyi Düzenle' : 'Yeni Muayene'}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingExam(null);
                  setFormData({
                    pet_id: '',
                    date: todayLocal(),
                    symptoms: '',
                    diagnosis: '',
                    treatment: '',
                    prescription: '',
                    next_visit: ''
                  });
                }}
                className="p-1.5 text-[#B0A99A] hover:text-[#8A8676] hover:bg-[#F0EBE1] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitForm} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Hasta + Tarih */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-[#2B2B2B]">
                      Hasta <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="pet_id"
                      value={formData.pet_id}
                      onChange={handleFormChange}
                      className={`w-full rounded-xl border-2 px-3 py-2.5 text-sm bg-white outline-none focus:ring-4 transition-all ${
                        formData.pet_id
                          ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20 bg-emerald-50/30'
                          : 'border-[#E8E2D4] focus:border-teal-400 focus:ring-teal-500/20'
                      }`}
                      required
                    >
                      <option value="">Hasta seçiniz...</option>
                      {pets.map(pet => (
                        <option key={pet.id} value={pet.id}>
                          {pet.name} {pet.owner?.name ? `· ${pet.owner.name}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-[#2B2B2B]">
                      Muayene Tarihi <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border-2 border-[#E8E2D4] focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white"
                      required
                    />
                  </div>
                </div>

                {/* Belirtiler */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-[#2B2B2B]">
                    Belirtiler <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="symptoms"
                    value={formData.symptoms}
                    onChange={handleFormChange}
                    rows={2}
                    placeholder="Hastada gözlenen belirtiler..."
                    className="w-full rounded-xl border-2 border-[#E8E2D4] focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white resize-none"
                    required
                  />
                </div>

                {/* Teşhis */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-[#2B2B2B]">
                    Teşhis <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="diagnosis"
                    value={formData.diagnosis}
                    onChange={handleFormChange}
                    rows={2}
                    placeholder="Konulan teşhis..."
                    className="w-full rounded-xl border-2 border-[#E8E2D4] focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white resize-none"
                    required
                  />
                </div>

                {/* Tedavi */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-[#2B2B2B]">
                    Tedavi <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="treatment"
                    value={formData.treatment}
                    onChange={handleFormChange}
                    rows={2}
                    placeholder="Uygulanan tedavi..."
                    className="w-full rounded-xl border-2 border-[#E8E2D4] focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white resize-none"
                    required
                  />
                </div>

                {/* Reçete */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-[#2B2B2B]">
                    Reçete <span className="text-[#B0A99A] font-normal">(Opsiyonel)</span>
                  </label>
                  <textarea
                    name="prescription"
                    value={formData.prescription}
                    onChange={handleFormChange}
                    rows={2}
                    placeholder="Reçete detayları..."
                    className="w-full rounded-xl border-2 border-[#E8E2D4] focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white resize-none"
                  />
                </div>

                {/* Sonraki ziyaret */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-[#2B2B2B]">
                    Sonraki Kontrol <span className="text-[#B0A99A] font-normal">(Opsiyonel)</span>
                  </label>
                  <input
                    type="date"
                    name="next_visit"
                    value={formData.next_visit}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border-2 border-[#E8E2D4] focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-4 border-t border-[#E8E2D4] bg-white shrink-0 rounded-b-2xl">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingExam(null);
                  }}
                  className="btn btn-secondary flex-1"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={!formData.pet_id || !formData.symptoms || !formData.diagnosis || !formData.treatment}
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {editingExam ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Pet History Modal */}
      {showHistoryModal && selectedPet && createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}>
          <div className="dribbble-panel max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#E8E2D4]">
              <div>
                <h2 className="text-xl font-semibold text-[#2B2B2B]">
                  {selectedPet.name} - İşlem Geçmişi
                </h2>
                <p className="text-sm text-[#8A8676]">
                  {selectedPet.breed} • {selectedPet.owner?.name}
                </p>
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-2 text-[#B0A99A] hover:text-[#8A8676] hover:bg-[#F0EBE1] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {(() => {
                const history = getPetHistory(selectedPet.id);
                return history.length > 0 ? (
                  <div className="space-y-4">
                    {history.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <motion.div
                          key={`${item.type}-${item.id}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="border border-[#E8E2D4] rounded-lg p-4 hover:bg-[#F7F4ED] transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <div className={`p-2 rounded-lg ${item.color}`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <span className={`px-2 py-1 text-xs rounded-full ${item.color}`}>
                                    {item.label}
                                  </span>
                                  <span className="text-sm text-[#8A8676]">
                                    {formatDate(item.date)}
                                  </span>
                                                                     {'time' in item && item.time && (
                                     <span className="text-sm text-[#8A8676]">
                                       {item.time}
                                     </span>
                                   )}
                                </div>
                                
                                                                 {item.type === 'examination' && (
                                   <div className="space-y-2">
                                     {'symptoms' in item && item.symptoms && (
                                       <div>
                                         <p className="text-sm font-medium text-[#2B2B2B]">Belirtiler:</p>
                                         <p className="text-sm text-[#8A8676]">{item.symptoms}</p>
                                       </div>
                                     )}
                                     {'diagnosis' in item && item.diagnosis && (
                                       <div>
                                         <p className="text-sm font-medium text-[#2B2B2B]">Teşhis:</p>
                                         <p className="text-sm text-[#8A8676]">{item.diagnosis}</p>
                                       </div>
                                     )}
                                     {'treatment' in item && item.treatment && (
                                       <div>
                                         <p className="text-sm font-medium text-[#2B2B2B]">Tedavi:</p>
                                         <p className="text-sm text-[#8A8676]">{item.treatment}</p>
                                       </div>
                                     )}
                                     {'prescription' in item && item.prescription && (
                                       <div>
                                         <p className="text-sm font-medium text-[#2B2B2B]">Reçete:</p>
                                         <p className="text-sm text-[#8A8676]">{item.prescription}</p>
                                       </div>
                                     )}
                                   </div>
                                 )}
                                 
                                 {item.type === 'appointment' && (
                                   <div>
                                     {'notes' in item && item.notes && (
                                       <div>
                                         <p className="text-sm font-medium text-[#2B2B2B]">Notlar:</p>
                                         <p className="text-sm text-[#8A8676]">{item.notes}</p>
                                       </div>
                                     )}
                                   </div>
                                 )}
                                 
                                                                   {item.type === 'vaccination' && (
                                    <>
                                      {'vaccine_name' in item && item.vaccine_name && (
                                        <div>
                                          <p className="text-sm font-medium text-[#2B2B2B]">Aşı:</p>
                                          <p className="text-sm text-[#8A8676]">{item.vaccine_name}</p>
                                        </div>
                                      )}
                                      {'next_due_date' in item && item.next_due_date && (
                                        <div>
                                          <p className="text-sm font-medium text-[#2B2B2B]">Sonraki Aşı Tarihi:</p>
                                          <p className="text-sm text-[#8A8676]">{formatDate(item.next_due_date)}</p>
                                        </div>
                                      )}
                                    </>
                                  )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="w-12 h-12 text-[#B0A99A] mx-auto mb-3" />
                    <p className="text-[#8A8676]">Bu hasta için henüz işlem geçmişi bulunmuyor</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Silme Onay Modal */}
      <DeleteConfirmModal
        isOpen={!!examToDelete}
        onClose={() => setExamToDelete(null)}
        onConfirm={confirmDeleteExam}
        title="Muayene Kaydını Sil"
        message="Bu muayene kaydını silmek istediğinizden emin misiniz?"
        itemName={examToDelete?.diagnosis}
      />
    </div>
  );
};

export default Examinations; 